import React, { useCallback, useEffect, useState } from 'react';
import {
  listEvents,
  createEvent,
  updateEvent,
  getEventLive,
  listEventRegistrations,
  createOrGetConversation,
} from '../utils/api';
import { useToast } from './Toast';
import { FaPlus, FaCopy, FaWhatsapp, FaSync, FaEdit, FaCheck, FaTimes } from 'react-icons/fa';

const panel = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 14,
  padding: 16,
};

const statusLabel = (status, language) => {
  const map = {
    draft: { ar: 'مسودة', en: 'Draft' },
    live: { ar: 'مباشرة', en: 'Live' },
    ended: { ar: 'منتهية', en: 'Ended' },
  };
  const row = map[status] || { ar: status, en: status };
  return language === 'ar' ? row.ar : row.en;
};

const intentLabel = (intent, language) => {
  const map = {
    inquiry: { ar: 'استفسار', en: 'Inquiry' },
    purchase: { ar: 'شراء', en: 'Purchase' },
    visit: { ar: 'زيارة / معاينة', en: 'Visit' },
    rent: { ar: 'إيجار', en: 'Rent' },
    unknown: { ar: 'غير محدد', en: 'Unknown' },
  };
  const key = String(intent || 'unknown');
  const row = map[key];
  if (!row) return key;
  return language === 'ar' ? row.ar : row.en;
};

const Events = ({ language = 'ar', onNavigateToWhatsApp }) => {
  const isRTL = language === 'ar';
  const toast = useToast();
  const [events, setEvents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [live, setLive] = useState(null);
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', venue: '', status: 'live' });
  const [editForm, setEditForm] = useState({
    name: '',
    venue: '',
    description: '',
    status: 'live',
  });

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listEvents();
      const rows = res.data?.events || [];
      setEvents(rows);
      if (!selectedId && rows[0]) setSelectedId(rows[0].id);
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedId, toast]);

  const loadLive = useCallback(async (id) => {
    if (!id) return;
    try {
      const [l, r] = await Promise.all([
        getEventLive(id),
        listEventRegistrations(id, { limit: 80 }),
      ]);
      setLive(l.data);
      setRegs(r.data?.registrations || []);
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    }
  }, [toast]);

  useEffect(() => {
    loadList();
  }, []);

  useEffect(() => {
    if (!selectedId) return undefined;
    setEditing(false);
    loadLive(selectedId);
    const t = setInterval(() => loadLive(selectedId), 12000);
    return () => clearInterval(t);
  }, [selectedId, loadLive]);

  useEffect(() => {
    if (!live?.event || editing) return;
    setEditForm({
      name: live.event.name || '',
      venue: live.event.venue || '',
      description: live.event.description || '',
      status: live.event.status || 'live',
    });
  }, [live?.event?.id, live?.event?.name, live?.event?.venue, live?.event?.description, live?.event?.status, editing]);

  const onCreate = async () => {
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      const res = await createEvent(form);
      toast?.success?.(language === 'ar' ? 'تم إنشاء الفعالية' : 'Event created');
      setForm({ name: '', venue: '', status: 'live' });
      await loadList();
      if (res.data?.id) setSelectedId(res.data.id);
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    } finally {
      setCreating(false);
    }
  };

  const onSaveEdit = async () => {
    if (!selectedId || !editForm.name.trim()) {
      toast?.error?.(language === 'ar' ? 'الاسم مطلوب' : 'Name is required');
      return;
    }
    setSaving(true);
    try {
      await updateEvent(selectedId, {
        name: editForm.name.trim(),
        venue: editForm.venue.trim() || null,
        description: editForm.description.trim() || null,
        status: editForm.status,
      });
      toast?.success?.(language === 'ar' ? 'تم حفظ التعديلات' : 'Event updated');
      setEditing(false);
      await loadList();
      await loadLive(selectedId);
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const selected = events.find((e) => e.id === selectedId) || live?.event;
  const publicUrl =
    selected?.public_token && typeof window !== 'undefined'
      ? `${window.location.origin}/e/${selected.public_token}`
      : '';

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast?.success?.(language === 'ar' ? 'تم النسخ' : 'Copied');
    } catch (_) {
      toast?.error?.(language === 'ar' ? 'فشل النسخ' : 'Copy failed');
    }
  };

  const openWa = async (reg) => {
    try {
      const phone = String(reg.phone_normalized || reg.phone || '').replace(/\D/g, '');
      if (!phone) {
        toast?.error?.(language === 'ar' ? 'لا يوجد رقم' : 'No phone');
        return;
      }
      const res = await createOrGetConversation(phone);
      onNavigateToWhatsApp?.(res.data);
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    }
  };

  const maxIntent = Math.max(1, ...(live?.intents || []).map((i) => i.c));
  const inputStyle = { padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: '8px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: '0 0 4px', fontSize: '1.25rem' }}>
            {language === 'ar' ? 'الفعاليات' : 'Events'}
          </h2>
          <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>
            {language === 'ar'
              ? 'رابط مسح للتسجيل المباشر ← عملاء ووسوم في ريل ← متابعة واتساب أو حملة.'
              : 'QR link → register into Real clients + tags → WhatsApp / campaign follow-up.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => loadLive(selectedId)}
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            background: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: '0.85rem',
          }}
        >
          <FaSync /> {language === 'ar' ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      <div style={{ ...panel, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 10px', fontSize: '0.95rem', color: '#ea580c' }}>
          <FaPlus style={{ marginInlineEnd: 6 }} />
          {language === 'ar' ? 'فعالية جديدة' : 'New event'}
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr auto', gap: 10, maxWidth: 720 }}>
          <input
            placeholder={language === 'ar' ? 'اسم الفعالية / المعرض' : 'Event name'}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            style={inputStyle}
          />
          <input
            placeholder={language === 'ar' ? 'المكان' : 'Venue'}
            value={form.venue}
            onChange={(e) => setForm({ ...form, venue: e.target.value })}
            style={inputStyle}
          />
          <button
            type="button"
            onClick={onCreate}
            disabled={creating}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: 'none',
              background: '#ea580c',
              color: '#fff',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {creating ? '…' : language === 'ar' ? 'إنشاء' : 'Create'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 16 }}>
        <div style={{ ...panel, padding: 0, overflow: 'hidden', alignSelf: 'start' }}>
          {loading && <p style={{ padding: 12, color: '#94a3b8' }}>…</p>}
          {!loading && !events.length && (
            <p style={{ padding: 12, color: '#94a3b8', fontSize: '0.85rem' }}>
              {language === 'ar' ? 'لا فعاليات بعد' : 'No events yet'}
            </p>
          )}
          {events.map((ev) => (
            <button
              key={ev.id}
              type="button"
              onClick={() => setSelectedId(ev.id)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: isRTL ? 'right' : 'left',
                padding: '12px 14px',
                border: 'none',
                borderBottom: '1px solid #f1f5f9',
                background: ev.id === selectedId ? '#fff7ed' : '#fff',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{ev.name}</div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>
                {statusLabel(ev.status, language)} · {ev.registration_count || 0}{' '}
                {language === 'ar' ? 'تسجيل' : 'regs'}
              </div>
            </button>
          ))}
        </div>

        <div>
          {!selectedId && (
            <p style={{ color: '#94a3b8' }}>{language === 'ar' ? 'اختر فعالية' : 'Select an event'}</p>
          )}
          {selectedId && live && (
            <>
              <div style={{ ...panel, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: editing ? 12 : 0 }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    {!editing ? (
                      <>
                        <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{live.event?.name}</h3>
                        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.85rem' }}>
                          {live.event?.venue || '—'}
                          {live.event?.description ? ` · ${live.event.description}` : ''}
                        </p>
                        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.8rem' }}>
                          {language === 'ar' ? 'وسم الحملة' : 'Campaign tag'}{' '}
                          <code style={{ fontSize: '0.75rem' }}>{live.campaign_tag}</code>
                        </p>
                      </>
                    ) : (
                      <div style={{ display: 'grid', gap: 8 }}>
                        <label style={{ fontSize: '0.8rem', color: '#475569' }}>
                          {language === 'ar' ? 'الاسم' : 'Name'}
                          <input
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            style={{ ...inputStyle, marginTop: 4 }}
                          />
                        </label>
                        <label style={{ fontSize: '0.8rem', color: '#475569' }}>
                          {language === 'ar' ? 'المكان' : 'Venue'}
                          <input
                            value={editForm.venue}
                            onChange={(e) => setEditForm({ ...editForm, venue: e.target.value })}
                            style={{ ...inputStyle, marginTop: 4 }}
                          />
                        </label>
                        <label style={{ fontSize: '0.8rem', color: '#475569' }}>
                          {language === 'ar' ? 'الوصف' : 'Description'}
                          <textarea
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            rows={2}
                            style={{ ...inputStyle, marginTop: 4, resize: 'vertical' }}
                          />
                        </label>
                        <label style={{ fontSize: '0.8rem', color: '#475569' }}>
                          {language === 'ar' ? 'الحالة' : 'Status'}
                          <select
                            value={editForm.status}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                            style={{ ...inputStyle, marginTop: 4 }}
                          >
                            <option value="draft">{statusLabel('draft', language)}</option>
                            <option value="live">{statusLabel('live', language)}</option>
                            <option value="ended">{statusLabel('ended', language)}</option>
                          </select>
                        </label>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    {!editing ? (
                      <>
                        <select
                          value={live.event?.status || 'live'}
                          onChange={async (e) => {
                            await updateEvent(selectedId, { status: e.target.value });
                            loadList();
                            loadLive(selectedId);
                          }}
                          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
                          aria-label={language === 'ar' ? 'الحالة' : 'Status'}
                        >
                          <option value="draft">{statusLabel('draft', language)}</option>
                          <option value="live">{statusLabel('live', language)}</option>
                          <option value="ended">{statusLabel('ended', language)}</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => setEditing(true)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 8,
                            border: '1px solid #cbd5e1',
                            background: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <FaEdit /> {language === 'ar' ? 'تعديل' : 'Edit'}
                        </button>
                        <button
                          type="button"
                          onClick={() => copy(publicUrl)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 8,
                            border: '1px solid #cbd5e1',
                            background: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <FaCopy /> {language === 'ar' ? 'نسخ رابط المسح' : 'Copy QR link'}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={onSaveEdit}
                          disabled={saving}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 8,
                            border: 'none',
                            background: '#ea580c',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <FaCheck /> {saving ? '…' : language === 'ar' ? 'حفظ' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditing(false)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 8,
                            border: '1px solid #cbd5e1',
                            background: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <FaTimes /> {language === 'ar' ? 'إلغاء' : 'Cancel'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {publicUrl && !editing && (
                  <p
                    style={{
                      marginTop: 10,
                      fontSize: '0.75rem',
                      wordBreak: 'break-all',
                      color: '#475569',
                      fontFamily: 'ui-monospace, monospace',
                    }}
                  >
                    {publicUrl}
                  </p>
                )}
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                {[
                  [language === 'ar' ? 'مسوحات' : 'Scans', live.scans],
                  [language === 'ar' ? 'تسجيلات' : 'Regs', live.registrations],
                  [
                    language === 'ar' ? 'تحويل %' : 'Conv %',
                    live.conversion_pct != null ? `${live.conversion_pct}%` : '—',
                  ],
                  [language === 'ar' ? 'بلا محادثة' : 'No WA yet', live.pending_wa_link],
                ].map(([label, val]) => (
                  <div key={label} style={{ ...panel, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{label}</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
                      {val}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 12 }}>
                <div style={panel}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '0.9rem' }}>
                    {language === 'ar' ? 'توزيع الاهتمام' : 'Intent breakdown'}
                  </h4>
                  {(live.intents || []).length === 0 && (
                    <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</p>
                  )}
                  {(live.intents || []).map((row) => (
                    <div key={row.intent} style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                        <span>{intentLabel(row.intent, language)}</span>
                        <span>{row.c}</span>
                      </div>
                      <div style={{ height: 6, background: '#f1f5f9', borderRadius: 4, marginTop: 4 }}>
                        <div
                          style={{
                            width: `${(row.c / maxIntent) * 100}%`,
                            height: '100%',
                            background: '#ea580c',
                            borderRadius: 4,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                  <p style={{ marginTop: 12, fontSize: '0.75rem', color: '#64748b' }}>
                    {language === 'ar'
                      ? 'للحملة: فلتر الوسوم بهذا الوسم'
                      : 'For campaigns: filter by this tag'}{' '}
                    <code>{live.campaign_tag}</code>
                  </p>
                </div>

                <div style={{ ...panel, padding: 0, overflow: 'hidden' }}>
                  <h4 style={{ margin: 0, padding: '12px 14px', fontSize: '0.9rem', borderBottom: '1px solid #f1f5f9' }}>
                    {language === 'ar' ? 'آخر التسجيلات' : 'Live feed'}
                  </h4>
                  <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                    {regs.map((r) => (
                      <div
                        key={r.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 8,
                          padding: '10px 14px',
                          borderBottom: '1px solid #f8fafc',
                          fontSize: '0.82rem',
                        }}
                      >
                        <div>
                          <strong>{r.name}</strong>
                          <span style={{ color: '#64748b', marginInlineStart: 8 }}>{r.phone}</span>
                          <div style={{ color: '#94a3b8', fontSize: '0.72rem', marginTop: 2 }}>
                            {intentLabel(r.intent, language)} ·{' '}
                            {r.channel === 'qr'
                              ? language === 'ar'
                                ? 'مسح'
                                : 'QR'
                              : r.channel === 'form'
                                ? language === 'ar'
                                  ? 'نموذج'
                                  : 'Form'
                                : r.channel}{' '}
                            · {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          title="WhatsApp"
                          onClick={() => openWa(r)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#25D366',
                            cursor: 'pointer',
                            fontSize: '1.1rem',
                          }}
                        >
                          <FaWhatsapp />
                        </button>
                      </div>
                    ))}
                    {!regs.length && (
                      <p style={{ padding: 14, color: '#94a3b8', fontSize: '0.85rem' }}>
                        {language === 'ar'
                          ? 'لا تسجيلات — افتح رابط المسح واختبر النموذج'
                          : 'No regs yet — open the QR link and submit the form'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Events;
